import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, LOGO_SRC } from "../lib/content";

const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  const offset = 80;
  const top = el.getBoundingClientRect().top + window.scrollY - offset;
  window.scrollTo({ top, behavior: "smooth" });
};

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("");

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 24);
      const sections = NAV_LINKS.map((n) => document.getElementById(n.to)).filter(Boolean);
      const y = window.scrollY + 140;
      let current = "";
      for (const s of sections) {
        if (s.offsetTop <= y) current = s.id;
      }
      setActive(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = (id) => {
    setOpen(false);
    scrollToSection(id);
  };

  const goTop = () => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-ink/90 backdrop-blur-xl border-b border-ink-border" : "bg-transparent"
      }`}
    >
      <div className="container-editorial flex items-center justify-between py-5 md:py-6">
        <button
          type="button"
          onClick={goTop}
          data-testid="site-logo"
          className="flex items-center gap-3 group"
        >
          <img
            src={LOGO_SRC}
            alt="Kevin Tan"
            className="h-10 md:h-11 w-auto select-none"
            draggable={false}
          />
          <span className="hidden md:inline label-small text-grey-soft border-l border-ink-border pl-3">
            Retail Advisory
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-9" aria-label="Primary">
          <button
            type="button"
            onClick={goTop}
            data-testid="nav-home"
            className={`text-[0.78rem] uppercase tracking-[0.24em] transition-colors duration-300 ${
              active === "" ? "text-gold" : "text-ivory/70 hover:text-ivory"
            }`}
          >
            Home
          </button>
          {NAV_LINKS.map((link) => (
            <button
              key={link.to}
              type="button"
              onClick={() => handleClick(link.to)}
              data-testid={`nav-${link.label.toLowerCase()}`}
              className={`text-[0.78rem] uppercase tracking-[0.24em] transition-colors duration-300 ${
                active === link.to ? "text-gold" : "text-ivory/70 hover:text-ivory"
              }`}
            >
              {link.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => handleClick("contact")}
            data-testid="cta-engage-header"
            className="hidden md:inline-flex items-center justify-center px-6 py-3 bg-gold text-ink font-medium tracking-[0.18em] text-[0.72rem] uppercase hover:bg-gold-hover transition-colors duration-300"
          >
            Engage Kevin
          </button>
          <button
            type="button"
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((s) => !s)}
            className="lg:hidden p-2 text-ivory hover:text-gold transition-colors"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <div
        className={`lg:hidden overflow-hidden transition-all duration-500 ease-out border-t border-ink-border bg-ink/95 backdrop-blur-xl ${
          open ? "max-h-[520px]" : "max-h-0"
        }`}
        data-testid="mobile-menu-panel"
      >
        <div className="container-editorial py-6 flex flex-col gap-1">
          <button
            type="button"
            onClick={goTop}
            data-testid="mobile-nav-home"
            className="py-3 border-b border-ink-border text-left text-sm uppercase tracking-[0.22em] text-ivory/80"
          >
            Home
          </button>
          {NAV_LINKS.map((link) => (
            <button
              key={link.to}
              type="button"
              onClick={() => handleClick(link.to)}
              data-testid={`mobile-nav-${link.label.toLowerCase()}`}
              className={`py-3 border-b border-ink-border last:border-b-0 text-left text-sm uppercase tracking-[0.22em] ${
                active === link.to ? "text-gold" : "text-ivory/80"
              }`}
            >
              {link.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => handleClick("contact")}
            data-testid="cta-engage-mobile"
            className="mt-5 inline-flex items-center justify-center px-6 py-3 bg-gold text-ink font-medium tracking-[0.2em] text-xs uppercase"
          >
            Engage Kevin
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
